import { format } from "date-fns";
import { TFunction } from "i18next";

import {
  CALCULATOR_STAGES,
  sonderAfaForcedOptions,
  getGrowthDynamicOptions,
  energyEfficiencyClasses,
  getFamilyTaxableIncomeOptions,
  getSplitTaxableIncomeOptions,
  priceGrowthRelationOptions,
} from "constants/calculator";

import { getItemSelect } from "helpers/selectHelpers";
import {
  convertValueToLocaleForm,
  convertLocaleValueToNumber,
} from "helpers/convertValueToLocale";

export const getLoansOptions = ({ t, loans }: { t: TFunction; loans: any }) => {
  const efficencyLoans = loans.filter(
    (loan: any) => !loan.devName.includes("CLASSIC")
  );

  const options = efficencyLoans.map((loan: any) => {
    if (loan.devName === "KW") {
      return {
        label: t(
          "calculator_form.invest_object_parameters.building_standard.eh40"
        ),
        value: loan.devName,
      };
    }
    if (loan.devName === "QNG") {
      return {
        label: t(
          "calculator_form.invest_object_parameters.building_standard.eh40_ang"
        ),
        value: loan.devName,
      };
    }

    if (loan.devName === "UNKNOWN") {
      return {
        label: t(
          "calculator_form.invest_object_parameters.building_standard.unknown"
        ),
        value: null,
      };
    }

    return {
      label: loan.devName,
      value: loan.devName,
    };
  });

  return [
    {
      label: t(
        "calculator_form.invest_object_parameters.building_standard.unknown"
      ),
      value: "UNKNOWN",
    },
    {
      label: t(
        "calculator_form.invest_object_parameters.building_standard.none"
      ),
      value: null,
    },
    ...options,
  ];
};

const convertStagesToForm = (stages: any) => {
  const startStage = stages.find(
    (stage: any) => stage.type === CALCULATOR_STAGES.START_STAGE
  );
  const orderStage = stages.find(
    (stage: any) => stage.type === CALCULATOR_STAGES.PURCHASE_STAGE
  );
  const finishStage = stages.find(
    (stage: any) => stage.type === CALCULATOR_STAGES.FINISH_STAGE
  );
  const rentalIncomeStage = stages.find(
    (stage: any) => stage.type === CALCULATOR_STAGES.RENTAL_INCOME_STAGE
  );
  const buildStages = stages.filter(
    (stage: any) => stage.type === CALCULATOR_STAGES.BUILDING_STAGE
  );

  const orderDate =
    orderStage?.date || startStage?.date || format(new Date(), "yyyy-MM-dd");

  const sortedStages = [
    {
      type: CALCULATOR_STAGES.START_STAGE,
      date: startStage?.date || "",
      fee: convertValueToLocaleForm(startStage?.fee || "0"),
    },
    {
      type: CALCULATOR_STAGES.PURCHASE_STAGE,
      date: orderDate,
      fee: convertValueToLocaleForm(orderStage?.fee || "0"),
    },
    {
      type: CALCULATOR_STAGES.FINISH_STAGE,
      date: finishStage?.date || format(new Date(), "yyyy-MM-dd"),
      fee: convertValueToLocaleForm(finishStage?.fee || "0"),
    },
    {
      type: CALCULATOR_STAGES.RENTAL_INCOME_STAGE,
      date: rentalIncomeStage?.date || format(new Date(), "yyyy-MM-dd"),
      fee: convertValueToLocaleForm(rentalIncomeStage?.fee || "0"),
    },
    ...buildStages.map((stage: any) => ({
      type: CALCULATOR_STAGES.BUILDING_STAGE,
      date: stage?.date || format(new Date(), "yyyy-MM-dd"),
      fee: convertValueToLocaleForm(stage?.fee || "0"),
    })),
  ];

  return sortedStages;
};

export const getInitialValues = ({
  calculationValues,
  loans,
  t,
  defaultValues,
  isSonderAfaForced,
  defaultCalculationValues,
}) => {
  const classicLoan = loans?.find((loan: any) => loan.devName === "CLASSIC");
  const kwLoan = loans?.find((loan: any) => loan.devName === "KW");
  const qngLoan = loans?.find((loan: any) => loan.devName === "QNG");
  const familyTaxableIncomeOptions = getFamilyTaxableIncomeOptions(t);
  const splitTaxableIncomeOptions = getSplitTaxableIncomeOptions(t);

  if (defaultCalculationValues) {
    const loanYears = defaultCalculationValues?.client?.loanYears;
    const classicPostLoanName = defaultCalculationValues?.postLoans?.find((loan: any) =>
      loan.includes("CLASSIC")
    );

    const defaultValuesPostLoan = loans?.find(
      (loan: any) => loan.devName === classicPostLoanName
    );

    const defaultValuesPostLoanPriodIndex =
      defaultValuesPostLoan?.periods?.findIndex(
        (period: any) =>
          period.from <= loanYears * 12 && period.to >= loanYears * 12
      );

    const defaultValuesClassicLoan = defaultCalculationValues.loans?.find(
      (loan: any) => loan.name === "Classic"
    );
    const defaultValuesKwLoan = defaultCalculationValues.loans?.find(
      (loan: any) => loan.name === "KW"
    );
    const defaultValuesQngLoan = defaultCalculationValues.loans?.find(
      (loan: any) => loan.name === "QNG"
    );

    const classicLoanPeriodIndex = classicLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const kwLoanPeriodIndex = kwLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const qngLoanPeriodIndex = qngLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const classicLoanValue = {
      name: "Classic",
      devName:
        defaultValuesPostLoan?.devName ||
        defaultValuesClassicLoan?.devName ||
        classicLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesPostLoan?.periods?.[defaultValuesPostLoanPriodIndex]
          ?.percent ||
        defaultValuesClassicLoan?.startRate ||
        classicLoan?.periods?.[classicLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(
        defaultValuesPostLoan?.rate ||
        defaultValuesClassicLoan?.rate ||
        classicLoan?.rate
      ),
    };
    const kwLoanValue = {
      name: "KW",
      devName: defaultValuesKwLoan?.devName || kwLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesKwLoan?.startRate ||
        kwLoan?.periods?.[kwLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(defaultValuesKwLoan?.rate || kwLoan?.rate),
    };
    const qngLoanValue = {
      name: "QNG",
      devName: defaultValuesQngLoan?.devName || qngLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesQngLoan?.startRate ||
        qngLoan?.periods?.[qngLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(
        defaultValuesQngLoan?.rate || qngLoan?.rate
      ),
    };

    const specialLoan = (() => {
      if (defaultCalculationValues?.energyEfficiencyClass === "KW") return kwLoanValue;
      if (defaultCalculationValues?.energyEfficiencyClass === "QNG") return qngLoanValue;
      return null;
    })();

    const isSonderAfaForcedByDefault =
      defaultCalculationValues.isSonderAfaForced || isSonderAfaForced;

    const priceGrowthDate =
      defaultCalculationValues?.priceGrowth?.date ||
      defaultCalculationValues?.stages?.find(
        (stage: any) => stage.type === CALCULATOR_STAGES.FINISH_STAGE
      )?.date ||
      format(new Date(), "yyyy-MM-dd");

    return {
      client: {
        hasSpouse: getItemSelect(
          familyTaxableIncomeOptions,
          defaultCalculationValues.client.hasSpouse
        ),
        taxableIncomeSpouseOne: convertValueToLocaleForm(
          defaultCalculationValues.client.taxableIncomeSpouseOne
        ),
        taxableIncomeSpouseTwo: convertValueToLocaleForm(
          defaultCalculationValues.client.taxableIncomeSpouseTwo
        ),
        splitTaxableIncome: getItemSelect(
          splitTaxableIncomeOptions,
          defaultCalculationValues.client.splitTaxableIncome
        ),
        loanYears: defaultCalculationValues.client.loanYears || 30,
        capital: defaultCalculationValues.client.capital || 0,
        monthlySpendingLimit: defaultCalculationValues.client.monthlySpendingLimit || "",
        calculationType: "loanYears",
      },
      apartments: defaultCalculationValues?.apartments.map((apartment: any) => {
        const aptRentPerMonth =
          apartment.aptRentPerMonth ||
          apartment.aptRentPerMonthPerSqMeter * apartment.aptArea ||
          0;

        const aptPrice =
          apartment.aptPrice ||
          (apartment.pricePerSqMeter * apartment.aptArea).toFixed(2) ||
          "";

        const aptDinSize = apartment.aptDinSize || apartment.aptArea || null;

        return {
          aptPrice: convertValueToLocaleForm(aptPrice),
          aptArea: convertValueToLocaleForm(apartment.aptArea),
          aptRentPerMonth: convertValueToLocaleForm(aptRentPerMonth),
          aptDiscount: convertValueToLocaleForm(apartment.aptDiscount),
          aptPriceGrowth: convertValueToLocaleForm(apartment.aptPriceGrowth),
          aptPriceGrowthDynamic: getItemSelect(
            getGrowthDynamicOptions(t),
            apartment.aptPriceGrowthDynamic
          ),
          aptRentGrowth: convertValueToLocaleForm(apartment.aptRentGrowth),
          aptRentGrowthDynamic: getItemSelect(
            getGrowthDynamicOptions(t),
            apartment.aptRentGrowthDynamic
          ),
          aptLandShare: convertValueToLocaleForm(apartment.aptLandShare),
          aptFurniturePrice: convertValueToLocaleForm(
            apartment.aptFurniturePrice
          ),
          aptDinSize: aptDinSize ? convertValueToLocaleForm(aptDinSize) : null,
        };
      }),
      parkings: defaultCalculationValues?.parkings.map((parking: any) => ({
        parkingType: parking.parkingType || "",
        parkingPrice: convertValueToLocaleForm(parking.parkingPrice),
        parkingDiscount: convertValueToLocaleForm(parking.parkingDiscount),
        parkingPriceGrowth: convertValueToLocaleForm(
          parking.parkingPriceGrowth
        ),
        parkingPriceGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingPriceGrowthDynamic
        ),
        parkingRentPerMonth: convertValueToLocaleForm(
          parking.parkingRentPerMonth
        ),
        parkingRentGrowth: convertValueToLocaleForm(parking.parkingRentGrowth),
        parkingRentGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingRentGrowthDynamic
        ),
        parkingLandShare: convertValueToLocaleForm(parking.parkingLandShare),
        parkingDinSize: parking.parkingDinSize
          ? convertValueToLocaleForm(parking.parkingDinSize)
          : 0,
      })),
      priceGrowth: {
        relationType:
          getItemSelect(
            priceGrowthRelationOptions(t),
            defaultCalculationValues?.priceGrowth?.relationType
          ) || priceGrowthRelationOptions(t)[0],
        date: priceGrowthDate,
      },
      brokerFee: convertValueToLocaleForm(defaultCalculationValues?.brokerFee),
      isSonderAfaForced: getItemSelect(
        sonderAfaForcedOptions,
        isSonderAfaForcedByDefault
      ),
      managementCost: convertValueToLocaleForm(defaultCalculationValues?.managementCost),
      managementCostGrowth: convertValueToLocaleForm(
        defaultCalculationValues?.managementCostGrowth
      ),
      managementCostGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues?.managementCostGrowthDynamic
      ),
      maintenanceCostPerSqMeter: convertValueToLocaleForm(
        defaultCalculationValues?.maintenanceCostPerSqMeter
      ),
      maintenanceCostPerSqMeterGrowth: convertValueToLocaleForm(
        defaultCalculationValues?.maintenanceCostPerSqMeterGrowth
      ),
      maintenanceCostPerSqMeterGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues?.maintenanceCostPerSqMeterGrowthDynamic
      ),
      downtimePercentage: convertValueToLocaleForm(
        defaultCalculationValues?.downtimePercentage
      ),
      downtimePercentageDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues?.downtimePercentageDynamic
      ),
      propertyTax: convertValueToLocaleForm(defaultCalculationValues?.propertyTax),
      // rentStartDate: defaultCalculationValues?.rentStartDate || "",
      energyEfficiencyClass: defaultCalculationValues?.energyEfficiencyClass
        ? getItemSelect(
          energyEfficiencyClasses,
          defaultCalculationValues?.energyEfficiencyClass
        )
        : null,
      stages: convertStagesToForm(defaultCalculationValues?.stages),
      loans: [classicLoanValue, ...(specialLoan ? [specialLoan] : [])],
    };
  }

  if (defaultValues) {
    const loanYears = defaultValues?.client?.loanYears;
    const classicPostLoanName = defaultValues?.postLoans?.find((loan: any) =>
      loan.includes("CLASSIC")
    );

    const defaultValuesPostLoan = loans?.find(
      (loan: any) => loan.devName === classicPostLoanName
    );

    const defaultValuesPostLoanPriodIndex =
      defaultValuesPostLoan?.periods?.findIndex(
        (period: any) =>
          period.from <= loanYears * 12 && period.to >= loanYears * 12
      );

    const defaultValuesClassicLoan = defaultValues.loans?.find(
      (loan: any) => loan.name === "Classic"
    );
    const defaultValuesKwLoan = defaultValues.loans?.find(
      (loan: any) => loan.name === "KW"
    );
    const defaultValuesQngLoan = defaultValues.loans?.find(
      (loan: any) => loan.name === "QNG"
    );

    const classicLoanPeriodIndex = classicLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const kwLoanPeriodIndex = kwLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const qngLoanPeriodIndex = qngLoan?.periods?.findIndex(
      (period: any) =>
        period.from <= loanYears * 12 && period.to >= loanYears * 12
    );

    const classicLoanValue = {
      name: "Classic",
      devName:
        defaultValuesPostLoan?.devName ||
        defaultValuesClassicLoan?.devName ||
        classicLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesPostLoan?.periods?.[defaultValuesPostLoanPriodIndex]
          ?.percent ||
        defaultValuesClassicLoan?.startRate ||
        classicLoan?.periods?.[classicLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(
        defaultValuesPostLoan?.rate ||
        defaultValuesClassicLoan?.rate ||
        classicLoan?.rate
      ),
    };
    const kwLoanValue = {
      name: "KW",
      devName: defaultValuesKwLoan?.devName || kwLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesKwLoan?.startRate ||
        kwLoan?.periods?.[kwLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(defaultValuesKwLoan?.rate || kwLoan?.rate),
    };
    const qngLoanValue = {
      name: "QNG",
      devName: defaultValuesQngLoan?.devName || qngLoan?.devName,
      startRate: convertValueToLocaleForm(
        defaultValuesQngLoan?.startRate ||
        qngLoan?.periods?.[qngLoanPeriodIndex]?.percent
      ),
      rate: convertValueToLocaleForm(
        defaultValuesQngLoan?.rate || qngLoan?.rate
      ),
    };

    const specialLoan = (() => {
      if (defaultValues?.energyEfficiencyClass === "KW") return kwLoanValue;
      if (defaultValues?.energyEfficiencyClass === "QNG") return qngLoanValue;
      return null;
    })();

    const isSonderAfaForcedByDefault =
      defaultValues.isSonderAfaForced || isSonderAfaForced;

    const priceGrowthDate =
      defaultValues.priceGrowthDate ||
      defaultValues?.stages?.find(
        (stage: any) => stage.type === CALCULATOR_STAGES.FINISH_STAGE
      )?.date ||
      format(new Date(), "yyyy-MM-dd");

    return {
      client: {
        hasSpouse: getItemSelect(
          familyTaxableIncomeOptions,
          defaultValues.client.hasSpouse
        ),
        taxableIncomeSpouseOne: convertValueToLocaleForm(
          defaultValues?.client.taxableIncomeSpouseOne
        ),
        taxableIncomeSpouseTwo: convertValueToLocaleForm(
          defaultValues?.client.taxableIncomeSpouseTwo
        ),
        splitTaxableIncome: getItemSelect(
          splitTaxableIncomeOptions,
          defaultValues?.client.splitTaxableIncome
        ),
        loanYears: defaultValues?.client.loanYears || 30,
        capital: defaultValues?.client.capital || 0,
        monthlySpendingLimit: defaultValues?.client.monthlySpendingLimit || "",
        calculationType: "loanYears",
      },
      apartments: defaultValues?.apartments.map((apartment: any) => {
        const aptRentPerMonth =
          apartment.aptRentPerMonth ||
          apartment.aptRentPerMonthPerSqMeter * apartment.aptArea ||
          0;

        const aptPrice =
          apartment.aptPrice ||
          (apartment.pricePerSqMeter * apartment.aptArea).toFixed(2) ||
          "";

        const aptDinSize = apartment.aptDinSize || apartment.aptArea || null;

        return {
          aptPrice: convertValueToLocaleForm(aptPrice),
          aptArea: convertValueToLocaleForm(apartment.aptArea),
          aptRentPerMonth: convertValueToLocaleForm(aptRentPerMonth),
          aptDiscount: convertValueToLocaleForm(apartment.aptDiscount),
          aptPriceGrowth: convertValueToLocaleForm(apartment.aptPriceGrowth),
          aptPriceGrowthDynamic: getItemSelect(
            getGrowthDynamicOptions(t),
            apartment.aptPriceGrowthDynamic
          ),
          aptRentGrowth: convertValueToLocaleForm(apartment.aptRentGrowth),
          aptRentGrowthDynamic: getItemSelect(
            getGrowthDynamicOptions(t),
            apartment.aptRentGrowthDynamic
          ),
          aptLandShare: convertValueToLocaleForm(apartment.aptLandShare),
          aptFurniturePrice: convertValueToLocaleForm(
            apartment.aptFurniturePrice
          ),
          aptDinSize: aptDinSize ? convertValueToLocaleForm(aptDinSize) : null,
        };
      }),
      parkings: defaultValues?.parkings.map((parking: any) => ({
        parkingType: parking.parkingType || "",
        parkingPrice: convertValueToLocaleForm(parking.parkingPrice),
        parkingDiscount: convertValueToLocaleForm(parking.parkingDiscount),
        parkingPriceGrowth: convertValueToLocaleForm(
          parking.parkingPriceGrowth
        ),
        parkingPriceGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingPriceGrowthDynamic
        ),
        parkingRentPerMonth: convertValueToLocaleForm(
          parking.parkingRentPerMonth
        ),
        parkingRentGrowth: convertValueToLocaleForm(parking.parkingRentGrowth),
        parkingRentGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingRentGrowthDynamic
        ),
        parkingLandShare: convertValueToLocaleForm(parking.parkingLandShare),
        parkingDinSize: parking.parkingDinSize
          ? convertValueToLocaleForm(parking.parkingDinSize)
          : 0,
      })),
      priceGrowth: {
        relationType:
          getItemSelect(
            priceGrowthRelationOptions(t),
            defaultValues?.priceGrowth.relationType
          ) || priceGrowthRelationOptions(t)[0],
        date: priceGrowthDate,
      },
      brokerFee: convertValueToLocaleForm(defaultValues?.brokerFee),
      isSonderAfaForced: getItemSelect(
        sonderAfaForcedOptions,
        isSonderAfaForcedByDefault
      ),
      managementCost: convertValueToLocaleForm(defaultValues?.managementCost),
      managementCostGrowth: convertValueToLocaleForm(
        defaultValues?.managementCostGrowth
      ),
      managementCostGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultValues?.managementCostGrowthDynamic
      ),
      maintenanceCostPerSqMeter: convertValueToLocaleForm(
        defaultValues?.maintenanceCostPerSqMeter
      ),
      maintenanceCostPerSqMeterGrowth: convertValueToLocaleForm(
        defaultValues?.maintenanceCostPerSqMeterGrowth
      ),
      maintenanceCostPerSqMeterGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultValues?.maintenanceCostPerSqMeterGrowthDynamic
      ),
      downtimePercentage: convertValueToLocaleForm(
        defaultValues?.downtimePercentage
      ),
      downtimePercentageDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultValues?.downtimePercentageDynamic
      ),
      propertyTax: convertValueToLocaleForm(defaultValues?.propertyTax),
      // rentStartDate: defaultValues?.rentStartDate || "",
      energyEfficiencyClass:
        getItemSelect(
          energyEfficiencyClasses,
          defaultValues?.energyEfficiencyClass
        ) || null,
      stages: convertStagesToForm(defaultValues?.stages),
      loans: [classicLoanValue, ...(specialLoan ? [specialLoan] : [])],
    };
  }

  return {
    client: {
      hasSpouse: familyTaxableIncomeOptions[0],
      taxableIncomeSpouseOne: convertValueToLocaleForm(
        calculationValues?.taxableIncomeSpouseOne
      ),
      taxableIncomeSpouseTwo: convertValueToLocaleForm(
        calculationValues?.taxableIncomeSpouseTwo
      ),
      splitTaxableIncome: getItemSelect(
        splitTaxableIncomeOptions,
        calculationValues?.splitTaxableIncome
      ),
      loanYears: convertValueToLocaleForm(calculationValues?.loanYears),
      capital: convertValueToLocaleForm(calculationValues?.capital),
      monthlySpendingLimit: convertValueToLocaleForm(
        calculationValues?.monthlySpendingLimit
      ),
      calculationType: "loanYears",
    },
    apartments: [
      {
        aptPrice: convertValueToLocaleForm(calculationValues?.aptPrice),
        aptArea: convertValueToLocaleForm(calculationValues?.aptArea),
        aptRentPerMonth: convertValueToLocaleForm(
          calculationValues?.aptRentPerMonth
        ),
        aptDiscount: "0",
        aptPriceGrowth: convertValueToLocaleForm(
          calculationValues?.aptPriceGrowth
        ),
        aptPriceGrowthDynamic: getGrowthDynamicOptions(t)[0],
        aptRentGrowth: convertValueToLocaleForm(
          calculationValues?.aptRentGrowth
        ),
        aptRentGrowthDynamic: getGrowthDynamicOptions(t)[0],
        aptLandShare: convertValueToLocaleForm(calculationValues?.landShare),
        aptFurniturePrice: convertValueToLocaleForm(
          calculationValues?.aptFurniturePrice
        ),
        aptDinSize: null,
      },
    ],
    parkings: [],
    priceGrowth: {
      relationType: priceGrowthRelationOptions(t)[0],
      date: format(new Date(), "yyyy-MM-dd"),
    },
    brokerFee: convertValueToLocaleForm(0),
    isSonderAfaForced: sonderAfaForcedOptions[0],
    managementCost: convertValueToLocaleForm(calculationValues?.managementCost),
    managementCostGrowth: convertValueToLocaleForm(
      calculationValues?.managementCostGrowth
    ),
    managementCostGrowthDynamic: getGrowthDynamicOptions(t)[0],
    maintenanceCostPerSqMeter: convertValueToLocaleForm(
      calculationValues?.maintenancePerM2
    ),
    maintenanceCostPerSqMeterGrowth: convertValueToLocaleForm(
      calculationValues?.maintenanceCostPerSqMeterGrowth
    ),
    maintenanceCostPerSqMeterGrowthDynamic: getGrowthDynamicOptions(t)[0],
    downtimePercentage: convertValueToLocaleForm(
      calculationValues?.downtimePercentage
    ),
    downtimePercentageDynamic: getGrowthDynamicOptions(t)[0],
    propertyTax: convertValueToLocaleForm(calculationValues?.propertyTax),
    // rentStartDate: format(new Date(), "yyyy-MM-dd"),
    energyEfficiencyClass: energyEfficiencyClasses[0] || null,
    stages: [
      {
        type: "START_STAGE",
        date: "",
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "PURCHASE_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "FINISH_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "RENTAL_INCOME_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
    ],
    loans: [
      {
        name: "Classic",
        devName: classicLoan?.devName,
        startRate: convertValueToLocaleForm(classicLoan?.periods?.[0]?.percent),
        rate: convertValueToLocaleForm(classicLoan?.rate),
      },
    ],
  };
};

export const getZeroInitialValues = ({
  calculationValues,
  loans,
  t,
  defaultCalculationValues,
}) => {
  const classicLoan = loans?.find((loan: any) => loan.devName === "CLASSIC");
  const familyTaxableIncomeOptions = getFamilyTaxableIncomeOptions(t);
  const splitTaxableIncomeOptions = getSplitTaxableIncomeOptions(t);


  if (defaultCalculationValues) {
    return {
      client: {
        hasSpouse: familyTaxableIncomeOptions.find(
          (option: any) =>
            option.value === defaultCalculationValues.client.hasSpouse
        ),
        taxableIncomeSpouseOne: convertValueToLocaleForm(
          defaultCalculationValues.client.taxableIncomeSpouseOne
        ),
        taxableIncomeSpouseTwo: convertValueToLocaleForm(
          defaultCalculationValues.client.taxableIncomeSpouseTwo
        ),
        splitTaxableIncome: getItemSelect(
          splitTaxableIncomeOptions,
          defaultCalculationValues.client.splitTaxableIncome
        ),
        loanYears: convertValueToLocaleForm(
          defaultCalculationValues.client.loanYears
        ),
        capital: convertValueToLocaleForm(
          defaultCalculationValues.client.capital
        ),
        monthlySpendingLimit: convertValueToLocaleForm(
          defaultCalculationValues.client.monthlySpendingLimit
        ),
        calculationType: "loanYears",
      },

      apartments: defaultCalculationValues.apartments.map((apartment: any) => ({
        aptPrice: convertValueToLocaleForm(apartment.aptPrice),
        aptArea: convertValueToLocaleForm(apartment.aptArea),
        aptRentPerMonth: convertValueToLocaleForm(
          apartment.aptRentPerMonthPerSqMeter * apartment.aptArea
        ),
        aptDiscount: convertValueToLocaleForm(apartment.aptDiscount),
        aptPriceGrowth: convertValueToLocaleForm(apartment.aptPriceGrowth),
        aptPriceGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          apartment.aptPriceGrowthDynamic
        ),
        aptRentGrowth: convertValueToLocaleForm(apartment.aptRentGrowth),
        aptRentGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          apartment.aptRentGrowthDynamic
        ),
        aptLandShare: convertValueToLocaleForm(apartment.aptLandShare),
        aptFurniturePrice: convertValueToLocaleForm(
          apartment.aptFurniturePrice
        ),
        aptDinSize: apartment.aptDinSize === null
        ? null
        : convertValueToLocaleForm(apartment.aptDinSize) ,
      })),
      parkings: defaultCalculationValues.parkings.map((parking: any) => ({
        parkingPrice: convertValueToLocaleForm(parking.parkingPrice),
        parkingDiscount: convertValueToLocaleForm(parking.parkingDiscount),
        parkingPriceGrowth: convertValueToLocaleForm(
          parking.parkingPriceGrowth
        ),
        parkingPriceGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingPriceGrowthDynamic
        ),
        parkingRentPerMonth: convertValueToLocaleForm(
          parking.parkingRentPerMonth
        ),
        parkingRentGrowth: convertValueToLocaleForm(parking.parkingRentGrowth),
        parkingRentGrowthDynamic: getItemSelect(
          getGrowthDynamicOptions(t),
          parking.parkingRentGrowthDynamic
        ),
        parkingLandShare: convertValueToLocaleForm(parking.parkingLandShare),
        parkingDinSize: convertValueToLocaleForm(parking.parkingDinSize),
        parkingType: parking.parkingType || "",
      })),
      priceGrowth: {
        relationType: getItemSelect(
          priceGrowthRelationOptions(t),
          defaultCalculationValues.priceGrowth.relationType
        ),
        date: defaultCalculationValues.priceGrowth.date,
      },
      brokerFee: convertValueToLocaleForm(defaultCalculationValues.brokerFee),
      isSonderAfaForced: getItemSelect(
        sonderAfaForcedOptions,
        defaultCalculationValues.isSonderAfaForced
      ),
      managementCost: convertValueToLocaleForm(
        defaultCalculationValues.managementCost
      ),
      managementCostGrowth: convertValueToLocaleForm(
        defaultCalculationValues.managementCostGrowth
      ),
      managementCostGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues.managementCostGrowthDynamic
      ),
      maintenanceCostPerSqMeter: convertValueToLocaleForm(
        defaultCalculationValues.maintenanceCostPerSqMeter
      ),
      maintenanceCostPerSqMeterGrowth: convertValueToLocaleForm(
        defaultCalculationValues.maintenanceCostPerSqMeterGrowth
      ),
      maintenanceCostPerSqMeterGrowthDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues.maintenanceCostPerSqMeterGrowthDynamic
      ),
      downtimePercentage: convertValueToLocaleForm(
        defaultCalculationValues.downtimePercentage
      ),
      downtimePercentageDynamic: getItemSelect(
        getGrowthDynamicOptions(t),
        defaultCalculationValues.downtimePercentageDynamic
      ),
      propertyTax: convertValueToLocaleForm(
        defaultCalculationValues.propertyTax
      ),
      // rentStartDate: format(new Date(), "yyyy-MM-dd"), //TODO ?????
      energyEfficiencyClass: defaultCalculationValues.energyEfficiencyClass
        ? getItemSelect(
          energyEfficiencyClasses,
          defaultCalculationValues.energyEfficiencyClass
        )
        : null,
      stages: defaultCalculationValues.stages.map((stage: any) => ({
        type: stage.type,
        date: stage.date,
        fee: convertValueToLocaleForm(stage.fee),
      })),
      loans: defaultCalculationValues.loans.map((loan: any) => ({
        name: loan.name,
        devName: loan.devName,
        startRate: convertValueToLocaleForm(loan.startRate),
        rate: convertValueToLocaleForm(loan.rate),
      })),
    };
  }

  return {
    client: {
      hasSpouse: familyTaxableIncomeOptions[0],
      taxableIncomeSpouseOne: "",
      taxableIncomeSpouseTwo: 0,
      splitTaxableIncome: getItemSelect(
        splitTaxableIncomeOptions,
        calculationValues?.splitTaxableIncome
      ),
      loanYears: convertValueToLocaleForm(calculationValues?.loanYears),
      capital: convertValueToLocaleForm(calculationValues?.capital),
      monthlySpendingLimit: convertValueToLocaleForm(
        calculationValues?.monthlySpendingLimit
      ),
      calculationType: "loanYears",
    },
    apartments: [
      {
        aptPrice: "",
        aptArea: "",
        aptRentPerMonth: "",
        aptDiscount: "0",
        aptPriceGrowth: convertValueToLocaleForm(
          calculationValues?.aptPriceGrowth
        ),
        aptPriceGrowthDynamic: getGrowthDynamicOptions(t)[0],
        aptRentGrowth: convertValueToLocaleForm(
          calculationValues?.aptRentGrowth
        ),
        aptRentGrowthDynamic: getGrowthDynamicOptions(t)[0],
        aptLandShare: convertValueToLocaleForm(calculationValues?.landShare),
        aptFurniturePrice: convertValueToLocaleForm(
          calculationValues?.aptFurniturePrice
        ),
        aptDinSize: null,
      },
    ],
    parkings: [],
    priceGrowth: {
      relationType: priceGrowthRelationOptions(t)[0],
      date: format(new Date(), "yyyy-MM-dd"),
    },
    brokerFee: convertValueToLocaleForm(0),
    isSonderAfaForced: sonderAfaForcedOptions[0],
    managementCost: convertValueToLocaleForm(calculationValues?.managementCost),
    managementCostGrowth: convertValueToLocaleForm(
      calculationValues?.managementCostGrowth
    ),
    managementCostGrowthDynamic: getGrowthDynamicOptions(t)[0],
    maintenanceCostPerSqMeter: convertValueToLocaleForm(
      calculationValues?.maintenancePerM2
    ),
    maintenanceCostPerSqMeterGrowth: convertValueToLocaleForm(
      calculationValues?.maintenanceCostPerSqMeterGrowth
    ),
    maintenanceCostPerSqMeterGrowthDynamic: getGrowthDynamicOptions(t)[0],
    downtimePercentage: convertValueToLocaleForm(
      calculationValues?.downtimePercentage
    ),
    downtimePercentageDynamic: getGrowthDynamicOptions(t)[0],
    propertyTax: convertValueToLocaleForm(calculationValues?.propertyTax),
    // rentStartDate: format(new Date(), "yyyy-MM-dd"),
    energyEfficiencyClass: energyEfficiencyClasses[0] || null,
    stages: [
      {
        type: "START_STAGE",
        date: "",
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "PURCHASE_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "FINISH_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
      {
        type: "RENTAL_INCOME_STAGE",
        date: format(new Date(), "yyyy-MM-dd"),
        fee: convertValueToLocaleForm("0"),
      },
    ],
    loans: [
      {
        name: "Classic",
        devName: classicLoan?.devName,
        startRate: convertValueToLocaleForm(classicLoan?.periods?.[0]?.percent),
        rate: convertValueToLocaleForm(classicLoan?.rate),
      },
    ],
  };
};

export const getDefaultApartmentValues = (
  calculationValues: any,
  t: TFunction
) => {
  return {
    aptPrice: convertValueToLocaleForm(calculationValues?.aptPrice),
    aptArea: convertValueToLocaleForm(calculationValues?.aptArea),
    aptRentPerMonth: convertValueToLocaleForm(
      calculationValues?.aptRentPerMonth
    ),
    aptDiscount: convertValueToLocaleForm(0),
    aptPriceGrowth: convertValueToLocaleForm(calculationValues?.aptPriceGrowth),
    aptPriceGrowthDynamic: getGrowthDynamicOptions(t)[0],
    aptRentGrowth: convertValueToLocaleForm(calculationValues?.aptRentGrowth),
    aptRentGrowthDynamic: getGrowthDynamicOptions(t)[0],
    aptLandShare: convertValueToLocaleForm(calculationValues?.landShare),
    aptFurniturePrice: convertValueToLocaleForm(0),
    aptDinSize: null,
  };
};

export const getDefaultParkingValues = (
  calculationValues: any,
  t: TFunction
) => {
  return {
    parkingPrice: convertValueToLocaleForm(calculationValues?.parkingPrice),
    parkingDiscount: convertValueToLocaleForm(0),
    parkingPriceGrowth: convertValueToLocaleForm(
      calculationValues?.parkingPriceGrowth
    ),
    parkingPriceGrowthDynamic: getGrowthDynamicOptions(t)[0],
    parkingRentPerMonth: convertValueToLocaleForm(
      calculationValues?.parkingRentPerMonth
    ),
    parkingRentGrowth: convertValueToLocaleForm(
      calculationValues?.parkingRentGrowth
    ),
    parkingRentGrowthDynamic: getGrowthDynamicOptions(t)[0],
    parkingLandShare: convertValueToLocaleForm(calculationValues?.landShare),
    parkingDinSize: 0,
  };
};

export const convertDataToCalculatorApi = (values: any) => {
  const isMarried = values.client.hasSpouse?.value;

  return {
    client: {
      hasSpouse: values.client.hasSpouse.value,
      taxableIncomeSpouseOne: convertLocaleValueToNumber(
        values.client.taxableIncomeSpouseOne
      ),
      taxableIncomeSpouseTwo: isMarried
        ? convertLocaleValueToNumber(values.client.taxableIncomeSpouseTwo)
        : 0,
      splitTaxableIncome: isMarried
        ? values.client.splitTaxableIncome.value
        : false,
      loanYears: convertLocaleValueToNumber(values.client.loanYears),
      capital: convertLocaleValueToNumber(values.client.capital),
      monthlySpendingLimit: convertLocaleValueToNumber(
        values.client.monthlySpendingLimit
      ),
    },
    apartments: values.apartments.map((apartment: any) => {
      const aptRentPerMonthPerSqMeter =
        Math.round(
          (convertLocaleValueToNumber(apartment.aptRentPerMonth) * 100) /
          convertLocaleValueToNumber(apartment.aptArea)
        ) / 100;

      return {
        aptArea: convertLocaleValueToNumber(apartment.aptArea),
        aptPrice: convertLocaleValueToNumber(apartment.aptPrice),
        pricePerSqMeter:
          convertLocaleValueToNumber(apartment.aptPrice) /
          convertLocaleValueToNumber(apartment.aptArea),
        aptDiscount: convertLocaleValueToNumber(apartment.aptDiscount),
        aptPriceGrowth: convertLocaleValueToNumber(apartment.aptPriceGrowth),
        aptPriceGrowthDynamic: apartment.aptPriceGrowthDynamic.value,
        aptRentPerMonthPerSqMeter,
        aptRentGrowth: convertLocaleValueToNumber(apartment.aptRentGrowth),
        aptRentGrowthDynamic: apartment.aptRentGrowthDynamic.value,
        aptLandShare: convertLocaleValueToNumber(apartment.aptLandShare),
        aptFurniturePrice: convertLocaleValueToNumber(
          apartment.aptFurniturePrice
        ),
        aptDinSize: apartment.aptDinSize
          ? convertLocaleValueToNumber(apartment.aptDinSize)
          : null,
      };
    }),
    parkings: values.parkings.map((parking: any) => ({
      parkingType: parking.parkingType || "",
      parkingPrice: convertLocaleValueToNumber(parking.parkingPrice),
      parkingDiscount: convertLocaleValueToNumber(parking.parkingDiscount),
      parkingPriceGrowth: convertLocaleValueToNumber(
        parking.parkingPriceGrowth
      ),
      parkingPriceGrowthDynamic: parking.parkingPriceGrowthDynamic.value,
      parkingRentPerMonth: convertLocaleValueToNumber(
        parking.parkingRentPerMonth
      ),
      parkingRentGrowth: convertLocaleValueToNumber(parking.parkingRentGrowth),
      parkingRentGrowthDynamic: parking.parkingRentGrowthDynamic.value,
      parkingLandShare: convertLocaleValueToNumber(parking.parkingLandShare),
      parkingDinSize: parking.parkingDinSize
        ? convertLocaleValueToNumber(parking.parkingDinSize)
        : 0,
    })),
    priceGrowth: {
      relationType: values.priceGrowth.relationType.value,
      date: values.priceGrowth.date,
    },
    brokerFee: convertLocaleValueToNumber(values.brokerFee),
    isSonderAfaForced: Boolean(values.isSonderAfaForced?.value),
    managementCost: convertLocaleValueToNumber(values.managementCost),
    managementCostGrowth: convertLocaleValueToNumber(
      values.managementCostGrowth
    ),
    managementCostGrowthDynamic: values.managementCostGrowthDynamic.value,
    downtimePercentage: convertLocaleValueToNumber(values.downtimePercentage),
    downtimePercentageDynamic: values.downtimePercentageDynamic.value,
    maintenanceCostPerSqMeter: convertLocaleValueToNumber(
      values.maintenanceCostPerSqMeter
    ),
    maintenanceCostPerSqMeterGrowth: convertLocaleValueToNumber(
      values.maintenanceCostPerSqMeterGrowth
    ),
    maintenanceCostPerSqMeterGrowthDynamic:
      values.maintenanceCostPerSqMeterGrowthDynamic.value,
    propertyTax: convertLocaleValueToNumber(values.propertyTax),
    // rentStartDate: values.rentStartDate,
    energyEfficiencyClass: values.energyEfficiencyClass?.value || null,
    stages: values.stages.map((stage: any) => ({
      type: stage.type,
      date: stage.date,
      fee: convertLocaleValueToNumber(stage.fee),
    })),
    loans: values.loans.map((loan: any) => ({
      name: loan.name,
      startRate: convertLocaleValueToNumber(loan.startRate),
      rate: convertLocaleValueToNumber(loan.rate),
    })),
  };
};
